/**
 * Which call controls are enabled, and why one is not.
 *
 * Availability is decided from three inputs only: the server-authoritative agent
 * state, the call status, and the provider capability list. A control that the
 * provider cannot perform is disabled with the reason shown, never left clickable
 * to fail during a live call.
 */

import type { AgentStateName, CallDetail } from '@/lib/api/types';

export type ControlId =
  | 'answer'
  | 'hangup'
  | 'hold'
  | 'resume'
  | 'mute'
  | 'dtmf'
  | 'warm_transfer'
  | 'blind_transfer'
  | 'conference'
  | 'recording_pause'
  | 'leave'
  | 'disposition'
  | 'complete_acw';

export interface ControlAvailability {
  enabled: boolean;
  reason?: string;
}

const CAPABILITY_FOR: Partial<Record<ControlId, string>> = {
  answer: 'answer',
  hangup: 'hangup',
  hold: 'hold',
  resume: 'hold',
  mute: 'mute',
  dtmf: 'dtmf',
  warm_transfer: 'warm_transfer',
  blind_transfer: 'blind_transfer',
  conference: 'conference',
  recording_pause: 'recording_pause',
};

export interface ControlContext {
  agentState: AgentStateName | null;
  call: CallDetail | null;
  providerCapabilities: string[];
  userCapabilities: string[];
  hasDisposition: boolean;
  conferenceLegCount: number;
}

const USER_CAPABILITY_FOR: Partial<Record<ControlId, string>> = {
  answer: 'call:answer',
  hangup: 'call:control',
  hold: 'call:control',
  resume: 'call:control',
  mute: 'call:control',
  dtmf: 'call:control',
  leave: 'call:control',
  warm_transfer: 'call:transfer',
  blind_transfer: 'call:transfer',
  conference: 'call:conference',
  recording_pause: 'recording:pause',
};

export function controlAvailability(id: ControlId, context: ControlContext): ControlAvailability {
  const { agentState, call, providerCapabilities, userCapabilities } = context;

  const userCapability = USER_CAPABILITY_FOR[id];
  if (userCapability && !userCapabilities.includes(userCapability)) {
    return { enabled: false, reason: `Your role does not include ${userCapability}` };
  }

  const providerCapability = CAPABILITY_FOR[id];
  if (providerCapability && providerCapabilities.length > 0) {
    if (!providerCapabilities.includes(providerCapability)) {
      return { enabled: false, reason: `The telephony provider does not support ${providerCapability}` };
    }
  }

  switch (id) {
    case 'answer':
      return agentState === 'ringing' && call?.direction !== 'outbound'
        ? { enabled: true }
        : {
            enabled: false,
            reason:
              call?.direction === 'outbound'
                ? 'Outbound calls do not need to be answered'
                : 'There is no ringing call',
          };
    case 'hangup':
      return agentState === 'ringing' || agentState === 'on_call' || agentState === 'hold'
        ? { enabled: true }
        : { enabled: false, reason: 'There is no active call' };
    case 'hold':
      return agentState === 'on_call'
        ? { enabled: true }
        : { enabled: false, reason: agentState === 'hold' ? 'Already on hold' : 'No call is connected' };
    case 'resume':
      return agentState === 'hold'
        ? { enabled: true }
        : { enabled: false, reason: 'The caller is not on hold' };
    case 'mute':
    case 'dtmf':
    case 'recording_pause':
      return agentState === 'on_call' || agentState === 'hold'
        ? { enabled: true }
        : { enabled: false, reason: 'No call is connected' };
    case 'warm_transfer':
    case 'blind_transfer':
      return agentState === 'on_call' || agentState === 'hold'
        ? { enabled: true }
        : { enabled: false, reason: 'No call is connected' };
    case 'conference': {
      if (agentState !== 'on_call' && agentState !== 'hold') {
        return { enabled: false, reason: 'No call is connected' };
      }
      return context.conferenceLegCount >= 5
        ? { enabled: false, reason: 'A conference may contain at most five legs' }
        : { enabled: true };
    }
    case 'leave': {
      if (agentState !== 'on_call' && agentState !== 'hold') {
        return { enabled: false, reason: 'No call is connected' };
      }
      return context.conferenceLegCount > 2
        ? { enabled: true }
        : { enabled: false, reason: 'You are the only agent; end the call instead' };
    }
    case 'disposition':
      return agentState === 'acw' || call?.status === 'completed'
        ? { enabled: true }
        : { enabled: false, reason: 'A disposition can be submitted once the call ends' };
    case 'complete_acw': {
      if (agentState !== 'acw') return { enabled: false, reason: 'Not in After Call Work' };
      return context.hasDisposition
        ? { enabled: true }
        : { enabled: false, reason: 'A disposition is required before completing wrap-up' };
    }
    default:
      return { enabled: false, reason: 'Unknown control' };
  }
}
